import '../auth.scss';
import 'react-phone-number-input/style.css';

import { ReactComponent as Logo } from '@basqet-dashboard/common/assets/logo.svg';
import { Button, Dropdown, FormInput, PhoneNumberInput } from '@basqet-dashboard/common/components';
import { REVENUE_OPTIONS } from '@basqet-dashboard/common/config';
import { UserContext } from '@basqet-dashboard/common/context';
import { useCountries, useDocumentTitle } from '@basqet-dashboard/common/hooks';
import { useSignupMutation } from '@basqet-dashboard/common/hooks/mutations/auth';
import { formatErrorMessage, setupSchema } from '@basqet-dashboard/common/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { toast } from 'react-toastify';

import { SignupComplete } from './complete';

const getFormFieldName = (field) => {
  const fieldMapping = {
    phone_number: 'phone_number',
    country_id: 'country_id',
    first_name: 'first_name',
    last_name: 'last_name',
    business_name: 'business_name',
    yearly_revenue: 'yearly_revenue'
  };

  return fieldMapping[field] || field;
};

function Setup({ user }) {
  const { setUser } = useContext(UserContext);
  const history = useHistory();
  const hasUser = Boolean(Object.keys(user).length);

  if (!hasUser) {
    history.push('/signup');
  }
  const {
    control,
    register,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm({ mode: 'onChange', resolver: yupResolver(setupSchema) });

  const { data: countries } = useCountries();

  useDocumentTitle('Setup');

  const {
    mutateAsync: SignupAction,
    isLoading: isSignupLoading,
    data: signupSuccessData
  } = useSignupMutation();

  const onSubmit = async (body) => {
    await SignupAction(
      { ...body, ...user },
      {
        onSuccess: (data) => {
          const { merchantUser } = data;
          setUser(merchantUser);
        },
        onError: (error) => {
          const data = error?.response?.data ?? {};
          const validationErrors = data?.data ?? {};
          if (Object.keys(validationErrors).length > 0) {
            formatErrorMessage(error);
            Object.entries(validationErrors).forEach(([field, errorData]) => {
              const fieldName = getFormFieldName(field);

              if (fieldName && errorData?.message) {
                setError(fieldName, {
                  type: 'server',
                  message: errorData.message
                });
              }
            });
          } else {
            const message = data?.message ?? 'An error occurred';
            toast.error(message);
          }
          if (data?.data?.phone_number) {
            setError('phone_number', { type: 'custom', message: 'Invalid Phone number' });
          }
          const message = data?.message ?? '';
          if (message.includes('exists')) {
            history.push('/signup/basic');
          }
        }
      }
    );
  };

  return (
    <>
      <Logo />
      <div className="auth__content">
        {!signupSuccessData && (
          <>
            <div className="auth__stage">
              <div className="auth__stage__full" />
            </div>
            <h1 className="page__title">
              Almost <span className="text-cursive">there!</span>
            </h1>
          </>
        )}
        <div className="auth__card">
          {signupSuccessData ? (
            <SignupComplete userEmail={user?.email} />
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="form__grid">
                <FormInput
                  label="First name"
                  placeholder="First name"
                  name="firstName"
                  type="text"
                  error={errors.first_name && errors.first_name.message}
                  register={register('first_name')}
                />
                <FormInput
                  label="Last name"
                  placeholder="Last name"
                  name="lastName"
                  type="text"
                  error={errors.last_name && errors.last_name.message}
                  register={register('last_name')}
                />
              </div>
              <PhoneNumberInput
                label="Phone Number"
                control={control}
                error={errors.phone_number && errors.phone_number.message}
              />

              <Dropdown
                formLabel="Country of residence"
                placeholder="Enter your country of residence"
                name="country"
                options={countries || []}
                error={errors.country_id && errors.country_id.message}
                register={register('country_id')}
              />
              <FormInput
                label="Business name"
                placeholder="Enter your business name"
                name="businessName"
                type="text"
                error={errors.business_name && errors.business_name.message}
                register={register('business_name')}
              />
              <Dropdown
                formLabel="Yearly revenue"
                placeholder="Yearly revenue"
                name="yearlyRevenue"
                options={REVENUE_OPTIONS}
                error={errors.yearly_revenue && errors.yearly_revenue.message}
                register={register('yearly_revenue')}
              />
              <Button isFull type="submit" isLoading={isSignupLoading}>
                Finish setup
              </Button>
            </form>
          )}
        </div>
      </div>
    </>
  );
}

Setup.propTypes = {
  user: PropTypes.object
};

export { Setup };
